import { ManageDocumentComponent } from './manage-document/manage-document.component';
import {
  Component,
  HostListener,
  OnInit,
  TemplateRef,
  ViewChild,
  OnDestroy
} from '@angular/core';
import { Logger, GlobalInfoService, AuthenticationService } from '@app/core';
import {
  ActivityService,
  FlowTitle,
  HelperFlowService,
  NotifierService,
  SelectTramiteComponent,
  TaskService
} from '@app/flow';
import {
  Actividad,
  BaseControl,
  ConfirmComponent,
  DynamicFormComponent,
  Microproceso,
  Static,
  Tarea,
  Tramite,
  Const,
  TramiteBase,
  FormaListasChequeo,
} from '@app/shared';
import { find, isEmpty, merge, result, findIndex } from 'lodash';
import { Subscription } from 'rxjs/Subscription';
import { Action, ConfigShowItems, EventAction } from './btn-trigger';
import {
  CptFormService,
  BasicProcessConfigurationService,
  SelectTramiteService,
  StorageService,
  FlowType,
  FlowOrigin
} from './shared';
import { ManageActivityService } from './shared/manage-activity.service';
import { ManageTaskService } from './shared/manage-task.service';
import { ToastrService } from 'ngx-toastr';
import { TabsetComponent } from 'ngx-bootstrap/tabs';
import { environment } from '@env/environment';
import { ManagementCheckListFormComponent } from './management-check-list-form/management-check-list-form.component';
import { ScrollToService, ScrollToConfigOptions } from '@nicky-lenaers/ngx-scroll-to';
import { BsModalRef, BsModalService } from 'ngx-bootstrap';

const log = new Logger('BasicProcessConfigurationComponent');

// Define las acciones posibles para una tarea o actividad
type actionManage = 'edit' | 'run';
// Base para los contextos de las plantillas que gestionan un trozo del componente
interface ManageContext {
  event: any;
  controls: BaseControl<any>[];
  classBtnSubmit: string | Array<string>;
  valueBtnSubmit: string;
  title: string;
  isLoading: boolean;
  extra?: any;
}
// Define el contexto de la plantilla 'cptFormTpl'
interface ManageContextCpt extends ManageContext {
  event: EventAction;
}
// Define el contexto de la plantilla 'manageActivityTpl'
interface ManageContextActivity extends ManageContext {
  event: actionManage;
}
// Define el contexto de la plantilla 'manageTaskTpl'
interface ManageContextTask extends ManageContext {
  event: actionManage;
}

@Component({
  selector: 'iw-basic-process-configuration',
  templateUrl: './basic-process-configuration.component.html',
  styleUrls: ['./basic-process-configuration.component.scss'],
  providers: [
    CptFormService,
    SelectTramiteService,
    HelperFlowService,
    BasicProcessConfigurationService,
    ActivityService,
    TaskService,
    StorageService,
    ManageActivityService,
    ManageTaskService
  ]
})
export class BasicProcessConfigurationComponent implements OnInit, OnDestroy {
  // Define los tipos de permisos que tiene el usuario en sesión
  activateAction = {
    created: false,
    review: false,
    update: false,
    delete: false,
    duplicate: false,
    titles: false
  };
  // Referencia para ubicar scroll
  referenceScroll = '';
  // define cuando es una forma con listas de chequeo
  formCheckList = false;
  // Configuración para generar el título del flujo
  configFlowTitle: Partial<FlowTitle>;
  // Determina si se esta ejecutando una petición al backend
  isLoading = {
    flow: false,
    sync: false
  };
  // Muestra diferentes elementos
  show = {
    // Templates que renderizan los elementos de la vista
    firstTemplate: true,
    secondTemplate: false,
    // Botón para sicronizar el flujo
    btnSync: false,
  };
  // origen del flujo
  origin: FlowOrigin;
  // Contiene la informacion de las listas de chequeo de la forma seleccionada
  formsCheckListSelected: FormaListasChequeo;
  // Contiene las configuraciones de las listas de chequeo de la forma seleccionada
  optionsFormSelect: any = {};
  // Contexto para los datos de la plantilla cptFormTpl
  cptFormCtx: Partial<ManageContextCpt> = {};
  // Contexto para los datos de la plantilla manageActivityTpl
  manageActivityCtx: Partial<ManageContextActivity> = {};
  // Contexto para los datos de la plantilla manageTaskTpl
  manageTaskCtx: Partial<ManageContextTask> = {};
  // Plantillas encargadas de renderizar el contenido de la vista
  firstTemplate: TemplateRef<any>;
  secondTemplate: TemplateRef<any>;
  // Referencia de la modal
  modalRef: BsModalRef;
  // Determina si se muestran ayudas de debug
  showDebugHelpers = environment.showDebugHelpers;
  // Plantilas disponibles para renderizar en ng-container
  @ViewChild('cptFormTpl') cptFormTpl: TemplateRef<any>;
  @ViewChild('flowConfigTpl') flowConfigTpl: TemplateRef<any>;
  @ViewChild('manageTaskTpl') manageTaskTpl: TemplateRef<any>;
  // Referencia al formulario dinámico de crear y editar categorias, procesos y trámites
  @ViewChild('cptDyFormRef') cptDyFormRef: DynamicFormComponent;
  // Referencia al formulario dinámico de crear y editar tareas
  @ViewChild('taskDyFormRef') taskDyFormRef: DynamicFormComponent;
  // Referencia del control personalizado para el select de roles en tareas
  @ViewChild('selectRolesTpl') selectRolesTpl: TemplateRef<any>;
  // Referencia a la plantilla para el control personalizado de ng-select para los formularios o maestras
  @ViewChild('ngSelectFormulario') ngSelectFormulario: TemplateRef<any>;
  // Componente de confirmaciones
  @ViewChild(ConfirmComponent) confirmRef: ConfirmComponent;
  // Referencia al componente que controlas las tabs de tareas
  @ViewChild('taskTabs') taskTabs: TabsetComponent;
  // Componente select-tramite
  @ViewChild('stcRef') stcRef: SelectTramiteComponent;
  // Referencia al tab de documentos
  @ViewChild(ManageDocumentComponent) manageDocumentRef: ManageDocumentComponent;
  // Componente de configuracion de listas de chequeo
  @ViewChild(ManagementCheckListFormComponent) manageChecklistFormRef: ManagementCheckListFormComponent;
  // Compoennte de titulos
  @ViewChild('manageTitles') manageTitles: TemplateRef<any>;

  // Suscripción a las notificaciones de otros componentes
  private notifications: Subscription;

  constructor(
    public hfs: HelperFlowService,
    public ss: StorageService,
    public mas: ManageActivityService,
    public mts: ManageTaskService,
    private cptfs: CptFormService,
    private sts: SelectTramiteService,
    private ns: NotifierService,
    private bpcs: BasicProcessConfigurationService,
    private toastr: ToastrService,
    private gis: GlobalInfoService,
    private as: AuthenticationService,
    private scroll: ScrollToService,
    private modalService: BsModalService,
  ) {
    // determina los permisos del usuario en sesion
    this.activateAction.created = this.as.confirmPermission(
      Const.types_permissions.administrationProcess,
      Const.types_action_permissions.created,
    );
    this.activateAction.review = this.as.confirmPermission(
      Const.types_permissions.administrationProcess,
      Const.types_action_permissions.review,
    );
    this.activateAction.update = this.as.confirmPermission(
      Const.types_permissions.administrationProcess,
      Const.types_action_permissions.update,
    );
    this.activateAction.delete = this.as.confirmPermission(
      Const.types_permissions.administrationProcess,
      Const.types_action_permissions.delete,
    );
    this.activateAction.duplicate = this.as.confirmPermission(
      Const.types_permissions.administrationProcess,
      Const.types_action_permissions.duplicate,
    );
    this.activateAction.titles = this.as.confirmPermission(
      Const.types_permissions.administrationProcess,
      Const.types_action_permissions.created,
    );
    if (this.activateAction.titles) {
      this.activateAction.titles = this.as.confirmPermission(
        Const.types_permissions.administrationProcess,
        Const.types_action_permissions.update,
      );
    }

    log.debug(this.activateAction);
  }

  ngOnInit() {
    this.referenceScroll = '';
    this.gis.set({ title: 'Configuración básica de procesos' });
    // Escuchar todas las notificaciones de los elementos hijos del flujo
    this.notifications = this.ns.get().subscribe(n => {
      // define si la accion se puede realizar por los permisos del usuario
      let actAction = false;
      // Define si se ejecuta el evento solicitado
      let next = false;
      // Si se ejecutó el evento click en una tarea
      if (n.source === 'task') {
        let action: actionManage;
        // Definir la acción que se quiere hacer con la tarea
        switch (n.action) {
          case 'editTask':
            if (!this.stcRef.generic) {
              if (this.activateAction.update) {
                action = 'edit';
                next = true;
                actAction = true;
              }
            } else {
              this.toastr.warning('Los tramites genéricos no pueden ser modificados', 'Accion no permitida');
            }
            break;
          case 'runMicroProcess':
            action = 'run';
            next = true;
            actAction = true;
            break;
        }
        this.referenceScroll = 'actividad_' + n.data.task.tarea_actividad + 'tarea_' + n.data.task.orden;
        // Controla la acción requerida de la actividad
        if (next && actAction) {
          this.initManageTask(action, n.data);
        }
      }
    });
  }

  ngOnDestroy() {
    this.notifications.unsubscribe();
  }

  /**
   * Cierra el una plantilla y si hay un flujo activo lo muestra.
   *
   * @memberof BasicProcessConfigurationComponent
   */
  closeTpl(tpl?: TemplateRef<any>) {
    this.manageActivityCtx = {};
    this.manageTaskCtx = {};
    this.cptFormCtx = {};
    this.formCheckList = false;
    this.show.btnSync = true;
    if (tpl) {
      this.firstTemplate = tpl;
    } else {
      if (this.ss.flow) {
        this.firstTemplate = this.flowConfigTpl;
      } else {
        this.firstTemplate = null;
        this.show.btnSync = false;
      }
    }
    setTimeout(() => {
      this.scroll.scrollTo({
        target: this.referenceScroll
      } as ScrollToConfigOptions);

      this.referenceScroll = '';
    });
  }

  /**
   * Retrocede un nivel en el flujo.
   *
   * @memberof BasicProcessConfigurationComponent
   */
  goBack() {
    this.hfs.returnToPreviousFlow(
      // Trámite
      idTramite => this.selectedDefaultTramite(idTramite, 'tramiteInGoBack', true),
      // Microproceso
      idMicroProcess => this.runMicroProcess(idMicroProcess, true)
    );
  }

  /**
   * Destruye la template actual del flujo y la vuelve a cargar.
   * Esta función no debe usarse, solo es para casos desesperados
   * si se puede buscar el origen del problema que le impide que el flujo
   * actual no se actualize.
   *
   * @memberof BasicProcessConfigurationComponent
   */
  forceRefreshFlow() {
    this.firstTemplate = null;
    setTimeout(() => {
      this.firstTemplate = this.flowConfigTpl;
      this.show.btnSync = true;
      this.closeTpl();
    });
  }

  /**
   * Reacciona al evento de soltar una tecla.
   *
   * @param {*} e
   * @memberof BasicProcessConfigurationComponent
   */
  @HostListener('window:keyup', ['$event'])
  onKeyUp(e: any) {
    switch (e.keyCode) {
      // Escape
      case 27:
        this.closeTpl(this.flowConfigTpl);
        break;
    }
  }

  // --------- Selects categoria, proceso y trámite --------- >

  /**
   * Evento que se ejecuta cuando se selecciona una
   * acción (crear, editar, eliminar) para cualquier select
   * de categoria, proceso, trámite y proceso secundario.
   *
   * Nota: se usa la abreviación 'st' para referirse al
   * componente 'iw-select-tramite'.
   *
   * @param {EventAction} event
   * @memberof BasicProcessConfigurationComponent
   */
  async stClickedAction(event: EventAction) {
    if (!this.stcRef.generic) {
      // Crear o editar
      if (event.action !== 'delete') {
        const ctx = this.cptFormCtx;
        let oldData: any = {};
        // Definir la plantilla para el create y edit
        this.firstTemplate = this.cptFormTpl;
        this.show.btnSync = false;
        // Setear propiedades del contexto de la plantilla
        ctx.event = event;
        ctx.classBtnSubmit = event.action === 'edit' ? 'btn-warning' : 'btn-success';
        ctx.valueBtnSubmit = event.action === 'edit' ? 'Editar' : 'Crear';
        // Consultar datos necesarios en el editar
        if (event.action === 'edit') {
          oldData = await this.sts.get(event.categoryAction, this.stcRef[event.categoryAction]['id']);
        }

        switch (event.categoryAction) {
          case 'category':
            ctx.controls = this.cptfs.getFieldsCategory(oldData);
            ctx.title = `${ctx.valueBtnSubmit} categoría`;
            break;
          case 'process':
            oldData['proceso_categoria'] = this.stcRef.category.id;
            ctx.controls = this.cptfs.getFieldsProcess(
              this.ngSelectFormulario,
              merge(oldData, {
                categories: this.stcRef.categories.map(v => {
                  return { key: v.id, value: v.nombre };
                })
              })
            );
            ctx.title = `${ctx.valueBtnSubmit} proceso`;
            break;
          case 'tramite':
            const data = await this.sts.getDefaultStatusTramite();
            oldData['tramite_proceso'] = this.stcRef.process;
            ctx.controls = this.cptfs.getFieldsTramite(
              this.ngSelectFormulario,
              merge(oldData, {
                defaultStatusTramite: data.map(v => {
                  return { key: v.id, value: v.nombre };
                }),
                processes: this.stcRef.processes.map(v => {
                  return { key: v.id, value: v.nombre };
                })
              })
            );
            ctx.title = `${ctx.valueBtnSubmit} trámite`;
            break;
        }
      } else {
        // Eliminar
        this.confirmRef
          .new({
            question: '¿Esta seguro de continuar con la eliminación?',
            labelConfirm: 'si',
            labelDecline: 'no'
          })
          .then(async res => {
            if (res) {
              const deleted = await this.sts.delete(
                event.categoryAction, this.stcRef[event.categoryAction]['id']
              );
              if (deleted) {
                if (event.categoryAction === 'tramite') {
                  const removed = this.ss.removeLocalFlow(this.stcRef.tramite.id);
                  if (removed) {
                    this.configFlowTitle = null;
                  }
                }
                this.stcRef.reload(event.categoryAction);
              }
            }
          });
      }
    } else {
      this.toastr.warning('Los tramites genéricos no pueden ser modificados', 'Accion no permitida');
    }
  }

  /**
   * Recoge la información del formulario de creación y edición,
   * para categoría, proceso y trámite.
   * Nota: esto solo aplica para el crear y editar.
   *
   * @param {EventAction} event
   * @param {*} data
   * @memberof BasicProcessConfigurationComponent
   */
  cptSended(event: EventAction, data: any) {
    const action = event.action === 'edit' ? 'modificación' : 'creación';
    this.confirmRef
      .new({
        question: `¿Esta seguro de continuar con la ${action}?`,
        labelConfirm: 'si',
        labelDecline: 'no'
      })
      .then(async res => {
        if (res) {
          this.cptFormCtx.isLoading = true;
          if (event.action === 'edit') {
            data['id'] = this.stcRef[event.categoryAction]['id'];
          }
          try {
            const response = await this.sts.save(event, data);
            if (response) {
              if (event.action === 'create') {
                await this.stcRef.reload(event.categoryAction, result(response, 'id'));
              } else {
                await this.stcRef.reload(event.categoryAction, result(data, 'id'));
              }
              this.cptDyFormRef.reset();
              if (event.categoryAction === 'tramite') {
                if (event.action === 'create') {
                  this.selectedDefaultTramite(response as TramiteBase);
                } else {
                  if (this.configFlowTitle && this.configFlowTitle.name) {
                    this.configFlowTitle.name = result(data, 'nombre');
                  }
                }
              } else if (event.categoryAction === 'process') {
                if (event.action === 'create') {
                  await this.stcRef.reload('tramite');
                }
              } else if (event.categoryAction === 'category') {
                if (event.action === 'create') {
                  await this.stcRef.reload('process');
                  this.stcRef.tramite = null;
                  this.stcRef.tramites = [];
                }
              }
              this.closeTpl();
            }
          } finally {
            this.cptFormCtx.isLoading = false;
          }
        }
      });
  }

  /**
   * Valida la información de los selects y guía el comportamiento
   * del usuario bloqueando y desapareciendo opciones.
   *
   * @param {Action} action
   * @returns {Partial<ConfigShowItems>}
   * @memberof BasicProcessConfigurationComponent
   */
  cptShowItems(action: Action): Partial<ConfigShowItems> {
    const show: Partial<ConfigShowItems> = {};
    switch (action) {
      case 'create':
        show.process = !this.stcRef.category ? false : true;
        show.tramite = !this.stcRef.process ? false : true;
        break;
      case 'edit':
        show.category = !this.stcRef.category ? false : true;
        show.process = !this.stcRef.process ? false : true;
        show.tramite = !this.stcRef.tramite ? false : true;
        break;
      case 'delete':
        show.category = !this.stcRef.category || !isEmpty(this.stcRef.processes) ? false : true;
        show.process = !this.stcRef.process || !isEmpty(this.stcRef.tramites) ? false : true;
        show.tramite = !this.stcRef.tramite ? false : true;
        break;
    }
    return show;
  }

  // --------- Administrar flujo --------- >
  /**
   * Evento que se ejecuta al seleccionar un trámite por default.
   *
   * @param {(TramiteBase | number)} [tramiteBase]
   * @param {FlowType} [flowType='tramite']
   * @param {boolean} [goBack=false]
   * @param {boolean} [view=true]
   * @returns
   * @memberof BasicProcessConfigurationComponent
   */
  async selectedDefaultTramite(
    tramiteBase?: TramiteBase | number,
    flowType: FlowType = 'tramite',
    goBack: boolean = false,
    view: boolean = true
  ) {
    let id: number;
    if (!this.stcRef.generic) {
      id = <number>result(this.stcRef, 'tramite.id');
    } else {
      id = <number>result(this.stcRef, 'genericT.id');
    }
    this.origin = {
      category: <number>result(this.stcRef, 'category.id'),
      process: <number>result(this.stcRef, 'process.id'),
      tramite: id
    };

    // Definir el estado de sincronización del TRÁMITE
    let tramiteIsSynchronized = this.ss.getSynchronizationStatus('tramite');
    log.debug('tramiteIsSynchronized: ', tramiteIsSynchronized);

    // Confirmación de descartar cambios pendientes en el flujo
    if (
      !goBack && ((tramiteIsSynchronized || !this.mas.getRefineActivities().length) && this.ss.pendingChanges)
    ) {
      const res = await this.confirmRef
        .new({
          question: `¿Desea descartar los cambios realizados actualmente en el flujo?`,
          labelConfirm: 'si',
          labelDecline: 'no'
        });
      // Si hay cambios pendientes no se vuelve a consultar el flujo
      if (res) {
        // Descartar cambios pendientes
        this.ss.pendingChanges = false;
        this.ss.clearHistory();
      } else {
        return;
      }
    } else if (!goBack && (!tramiteIsSynchronized && this.mas.getRefineActivities().length)) {
      const res = await this.confirmRef
        .new({
          question: 'El flujo aun no ha sido sincronizado con la base de datos, ' +
                    'al realizar esta acción puede perder los cambios realizados.' +
                    '¿Desea continuar con la acción?',
          labelConfirm: 'si',
          labelDecline: 'no'
        });
      // Si hay cambios pendientes no se vuelve a consultar el flujo
      if (res) {
        // Descartar cambios pendientes
        this.ss.pendingChanges = false;
        this.ss.removeLocalFlow(this.hfs.flowsHistory[0]);
        this.ss.clearHistory();
      } else {
        return;
      }
    }

    // Continuar proceso normalmente
    if (tramiteBase) {
      if (typeof tramiteBase === 'number') {
        id = tramiteBase;
        if (!this.stcRef.generic) {
          this.stcRef.reload('tramite', id);
        } else {
          this.stcRef.reload('genericT', id);
        }
      } else {
        id = tramiteBase.id;
        if (!this.stcRef.generic) {
          this.stcRef.tramite = tramiteBase;
        } else {
          this.stcRef.genericT = tramiteBase;

        }
      }
    }
    // const id = idTramite ? idTramite : this.stcRef.tramite.id;
    if (id) {
      let flow = null;
      // Consultar el flujo desde LocalStorage
      if (goBack) {
        flow = <Tramite>this.ss.getFromLocal([this.hfs.flowsHistory[0]], 1);
      }
      // Consultar flujo de DB
      if (!flow) {
        this.isLoading.flow = true;
        flow = await this.bpcs.getDefaultTramite(id);
        this.isLoading.flow = false;
      }
      // Almacenar y mostrar flujo
      this.setFlow(flowType, flow, this.origin, false, true);
      // validar si el flujo no esta sincronizado y tiene tareas por guardar
      tramiteIsSynchronized = this.ss.getSynchronizationStatus('tramite');
      if ((!tramiteIsSynchronized && this.mas.getRefineActivities().length)) {
        this.ss.pendingChanges = true;
      }
      // Definir template para la vista principal
      if (view) {
        this.firstTemplate = this.flowConfigTpl;
        this.show.btnSync = true;
      }

    } else {
      log.error('Es necesario el id del trámite por default.');
    }
  }

  /**
   * Evento que se ejecuta al entrar a un microproceso por default.
   *
   * @param {(Tarea | number)} task
   * @param {boolean} [goBack=false]
   * @param {boolean} [clean=false]
   * @param {boolean} [view=true]
   * @memberof BasicProcessConfigurationComponent
   */
  async runMicroProcess(
    task: Tarea | number,
    goBack: boolean = false,
    clean: boolean = false,
    view: boolean = true
  ) {
    const idTask = typeof task === 'number' ? task : task.id;
    let id: number;
    if (!this.stcRef.generic) {
      id = <number>result(this.stcRef, 'tramite.id');
    } else {
      id = <number>result(this.stcRef, 'genericT.id');
    }
    this.origin = {
      category: <number>result(this.stcRef, 'category.id'),
      process: <number>result(this.stcRef, 'process.id'),
      tramite: id
    };
    let flow;
    // Validación: la tarea debe ser de tipo micro proceso
    if (typeof task !== 'number' && task.tarea_tipo.nombre !== Const.TYPES_TASK.microProcess) {
      log.debug(`No se puede tratar la tarea de tipo ${task.tarea_tipo.nombre} como un microproceso.`);
    }

    // Por defecto se consulta el microproceso guardado en el LocalStorage
    flow = <Microproceso>this.ss.getMicroProcess([idTask], true);

    // Si el microproceso existe en la DB
    if (idTask > 0) {
      // Si no se ha consultado el flujo por primera vez
      if (!(flow && flow.id === idTask)) {
        // Consultar flujo de DB
        this.isLoading.flow = true;
        flow = await this.bpcs.getDefaultMicroProces(idTask);
        this.isLoading.flow = false;
      }
    } else {
      // Establece que el flujo no esta sincronizado
      if (!flow) {
        // Crear microproceso en local
        flow = new Microproceso({
          id: idTask,
          descripcion: (<Tarea>task).nombre,
          tarea_estado: (<Tarea>task).tarea_estado,
          orden: (<Tarea>task).orden
        });
      }
    }
    // Almacenar y mostrar flujo
    this.setFlow('microproces', flow, this.origin, goBack, clean);
    // Definir template para la vista principal
    if (view) {
      this.firstTemplate = this.flowConfigTpl;
      this.show.btnSync = true;
    }
  }

  /**
   * Setea los datos usados para generar el flujo.
   *
   * @param {(Tramite | Microproceso)} flowDB
   * @param {boolean} [goBack=false] - Define si se esta ascendiendo de un microproceso.
   * @param {boolean} [clean=false] - Resetea el historial de flujo, y define el nivel 1.
   * @memberof BasicProcessConfigurationComponent
   */
  setFlow(
    flowType: FlowType,
    flow: Tramite | Microproceso,
    origin: FlowOrigin,
    goBack: boolean = false,
    clean: boolean = false
  ) {
    this.toggleCompletionMode();
    // Registrar el nivel del flujo
    // Nota: el nivel define si estoy parado o no en un microproceso
    this.hfs.updateFlowHistory(flow, goBack, clean);
    // Almacenar flujo en memoria
    this.ss.setFlow(flowType, flow, origin);
    // Configurar propiedades para generar el título del flujo
    this.configFlowTitle = merge(this.hfs.getFlowTitle(flow), {
      notSynchronized: !this.ss.isSynchronized
    });
  }

  // --------- Administrar de tareas --------- >

  /**
   * Consulta si la forma seleccionada para la tarea tiene listas de cheque que puedan ser configurdas
   *
   * @param {number} idForm
   * @memberof ProcessAutomationComponent
   */
  loadFormCheckList(idForm: number) {
    if (idForm) {
      this.bpcs.getFormCheckList(idForm).subscribe(form => {
        if (Object.keys(form).length) {
          this.formCheckList = true;
          this.formsCheckListSelected = form;
          if (idForm === this.manageTaskCtx.extra.task.tarea_forma[0].id_forma) {
            if (this.manageTaskCtx.extra.task.tarea_forma[0].opciones) {
              this.optionsFormSelect = this.manageTaskCtx.extra.task.tarea_forma[0].opciones;
            } else {
              this.optionsFormSelect = {};
            }
          } else {
            this.optionsFormSelect = {};
          }
        } else {
          this.formCheckList = false;
          this.formsCheckListSelected = undefined;
        }
      });
    } else {
      this.formCheckList = false;
      this.formsCheckListSelected = undefined;
    }
  }

  /**
   * Controla y ejecuta una acción definida para una tarea.
   * Si la tarea es un microproceso lo ejecuta.
   *
   * @param {actionManage} event
   * @param {*} [data]
   * @memberof BasicProcessConfigurationComponent
   */
  initManageTask(event: actionManage, data?: any) {
    // Contexto(conjunto de propiedades) usadas en la plantilla para crear y editar
    const ctx = this.manageTaskCtx;
    const task: Tarea = data.task;
    let activity: Actividad = data.activity;

    // Si hay una tarea pero no la actividad a la que pertenece
    if (task && !activity) {
      // Filtrar actividad de flujo local
      activity = find(this.mas.activities, { id: task.meta.idActivity });
    }
    if (event === 'edit') {
      // Definir información extra que pueda llegar a ser útil en la template
      ctx.extra = Static.copyObjet(
        data,
        [
          'asignationRoles',
          'responsibleRoles',
          'selectRolesTpl',
          'statusOptions',
          'typesOptions'
        ]
      );
      ctx.isLoading = true;
      // Definir propiedades para configurar el formulario dinámico
      let defaultData = {
        selectRolesTpl: this.selectRolesTpl
      };
      // Definir información importante para el editar
      if (event === 'edit') {
        ctx.extra.activity = activity;
        const roles = this.mts.destructureRoles(task.tarea_rol);
        defaultData = merge(defaultData, {
          asignationRoles: roles.assigned,
          responsibleRoles: roles.responsibles
        });
        if (task.tarea_forma && task.tarea_forma.length) {
          this.loadFormCheckList(task.tarea_forma[0].id_forma);
        }

      }
      const configControls = !data ? defaultData : merge(task, defaultData);
      // Generar controles del formulario
      ctx.controls = this.mts.getControlsForDynamicForm(configControls);
      // Definir variables de configuración de la plantilla
      ctx.title = 'Editar';
      ctx.valueBtnSubmit = ctx.title;
      ctx.classBtnSubmit = 'btn-warning';
      ctx.event = event;
      ctx.isLoading = false;
      // Definir plantilla encargada de mostrar el formulario
      this.firstTemplate = this.manageTaskTpl;
      this.show.btnSync = false;
    } else if (event === 'run') {
      // Ejecutar microproceso
      this.runMicroProcess(task);
    }
  }

  /**
   * Recoge la información del formulario de creación y edición,
   * de tareas.
   *
   * @param {('edit' | 'create')} action
   * @param {*} data
   * @memberof BasicProcessConfigurationComponent
   */
  sendedTask(event: actionManage, data: any) {
    // Crear copia del obj de los datos
    const copyData = Object.assign({}, data);
    // Contexto de la plantilla
    const ctx = this.manageTaskCtx;
    // Autoriza resetear el formulario principal
    let authorizeReset = false;
    copyData.tarea_forma = ctx.extra.task.tarea_forma;
    // Opciones lista de chequeo
    if (this.formCheckList) {
      copyData.tarea_forma[0]['opciones'] = this.manageChecklistFormRef.getSpecificRecords();
    }
    // Generar array de roles que pueden asignar y/o ser responsbles
    copyData.tarea_rol = this.mts.generateRoleFormat(copyData.asignationRoles, copyData.responsibleRoles);
    // Consultar documentos de la tarea
    // copyData.documentos = this.manageDocumentRef.getDocument();
    // Eliminar key innecesarias
    delete copyData.asignationRoles;
    delete copyData.responsibleRoles;
    // Determinar evento a realizar
    switch (event) {
      case 'edit':
        const copyTask = Object.assign({}, ctx.extra.task);
        // Limpiar Data conformato de la consulta del flujo
        // copyTask.documentos = [];
        const edited = this.updateTask(ctx.extra.activity, merge(copyTask, copyData));
        // Si se edita la tarea exitosamente
        if (edited) {
          authorizeReset = true;
          this.forceRefreshFlow();
        } else {
          log.debug(`Error: No se pudó editar la tarea: ${edited}`);
        }
        break;
    }
    if (authorizeReset) {
      this.taskDyFormRef.reset();
    }
  }

  /**
   * Se encarga de abrir el flujo del tramite en determinado modo del flujo.
   *
   * @param {boolean} active
   * @param {FlowMode} [flowMode='completion']
   * @memberof BasicProcessConfigurationComponent
   */
  toggleCompletionMode() {
    this.secondTemplate = null;
    this.show.firstTemplate = true;
    this.show.secondTemplate = false;
  }

  /**
   * Actualiza la tarea dentro de la actividad correspondiente
   *
   * @param {Actividad} activity
   * @param {Tarea} task
   * @returns {boolean}
   * @memberof BasicProcessConfigurationComponent
   */
  updateTask(activity: Actividad, task: Tarea): boolean {
    const edited = this.mts.edit(activity, task);
    // Si se edita la tarea exitosamente
    if (edited) {
      this.toastr.success(`Tarea actualizada correctamente.`);
      return true;
    }
    return false;
  }

  /**
   * Sincroniza el flujo de LocalStorage en la Base de Datos.
   *
   * @memberof BasicProcessConfigurationComponent
   */
  async sync() {
    this.isLoading.sync = true;
    const res = await this.ss.syncInDB();
    this.isLoading.sync = false;
    if (res) {
      this.selectedDefaultTramite();
    }
  }

  /**
   * Permite usar la función 'result' de lodash en la vista.
   *
   * @param {Object} obj
   * @param {string} _find
   * @returns {*}
   * @memberof BasicProcessConfigurationComponent
   */
  result(obj: Object, _find: string): any {
    return result(obj, _find);
  }

  /**
   * Se encarga de validar que el tramite a refrescar despues de la validacion si este seleccionado
   *
   * @param {number} idProcces
   * @memberof BasicProcessConfigurationComponent
   */
  validRefresProcessDuplication(idProcces: number) {
    if (this.stcRef.tramite && this.stcRef.tramite.id === idProcces) {
      this.selectedDefaultTramite();
    }
  }

  /**
   * Abrir modal de titulos del proceso
   *
   * @memberof ManagementAutomationComponent
   */
  openModalTitles() {
    if (this.stcRef.tramite.id) {
      this.modalRef = this.modalService.show(
        this.manageTitles,
        { class: 'modal-lg-personalized', ignoreBackdropClick: true }
      );
    } else {
      this.toastr.warning('Debe seleccionar un trámite', 'Acción no permitida');
    }
  }

  /**
   * Cerrar modal de titulos del proceso
   *
   * @memberof ManagementAutomationComponent
   */
  closeModalTitles() {
    this.modalRef.hide();
    this.modalRef = null;
  }
} // End class



// WEBPACK FOOTER //
// ./src/app/administration/basic-process-configuration/basic-process-configuration.component.ts